import React, { memo, useCallback, useMemo, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import type { PreloadedQuery } from 'react-relay';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { ImportWorkButton } from '@atlassian/jira-software-onboarding-invites/src/ui/import-work-button/async.tsx';
import { View } from '@atlassian/jira-software-view-settings/src/common/types/constant.tsx';
import { useShowQuickFilters } from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import ViewSettingsPopup from '@atlassian/jira-software-view-settings/src/ui/index.tsx';
import {
	ViewSettingsButton,
	ViewSettingsSyncFromLocalStorageWrapper,
} from '@atlassian/jira-software-view-settings/src/ui/view-settings-button/index.tsx';
import {
	ViewSettingsPanelOnboarding,
	ViewSettingsPanelButtonSpotlight,
} from '@atlassian/jira-software-view-settings/src/ui/view-settings-panel-onboarding/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../../feature-flags.tsx';
import { VIEW_SETTINGS_PANEL } from '../../../model/panels/constants.tsx';
import type { PanelType } from '../../../model/panels/types.tsx';
import type { CardEntryGroup } from '../../../model/types.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getIsCompanyManaged } from '../../../state/selectors/app/index.tsx';
import {
	getCardExtraFields,
	backlogHasDueDate,
	backlogHasStoryPoints,
	backlogHasEstimates,
	backlogHasPriority,
} from '../../../state/selectors/cards/index.tsx';
import { getConfig, isCriticalDataLoaded } from '../../../state/selectors/config/index.tsx';
import { useAllCardGroups } from '../../../state/state-hooks/use-all-card-groups.tsx';
import { useIsInsightsPanelOpen } from '../../../state/state-hooks/use-is-insights-panel-open.tsx';
import { useIsViewSettingsPanelOpen } from '../../../state/state-hooks/use-is-view-settings-panel-open.tsx';
import type { FilterBarProps } from './filter-bar/index.tsx';
import FilterSelectionBarContainer from './filter-selection-bar/index.tsx';
import { InsightsButton } from './insights-button/index.tsx';

export type OwnProps = {
	customFiltersQueryRef?: PreloadedQuery<viewBacklogCustomFiltersQuery> | null;
	versionFilterRelayQueryRef?: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null;
};

export type Props = {
	isBacklogWithSprints: boolean;
	rapidViewId: number;
	isClassic: boolean;
	showEpicAsPanel: boolean;
	isVersionsEnabled: boolean;
} & OwnProps & {
		FilterBar: ComponentType<FilterBarProps>;
		onTogglePanel: (panelType: PanelType) => void;
		onSubtasksExpandAll: (parentIds: string[]) => void;
		onSubtasksCollapseAll: () => void;
	};

export const ControlsBar = (props: Props) => {
	const {
		isBacklogWithSprints,
		rapidViewId,
		FilterBar,
		onTogglePanel,
		customFiltersQueryRef,
		versionFilterRelayQueryRef,
		isClassic,
		showEpicAsPanel,
		isVersionsEnabled,
		onSubtasksExpandAll,
		onSubtasksCollapseAll,
	} = props;
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const config = useBacklogSelector(getConfig);
	const isDataLoaded = useBacklogSelector(isCriticalDataLoaded);
	const epicCustomName = config.hierarchyNomenclature?.firstLevelName;

	const appEditions = useAppEditions();
	const handleOnToggle = useCallback(
		(panelType: PanelType) => {
			onTogglePanel(panelType);
		},
		[onTogglePanel],
	);
	const handleViewSettingPanelToggle = useCallback(() => {
		onTogglePanel(VIEW_SETTINGS_PANEL);
	}, [onTogglePanel]);

	const isInsightsOpen = useIsInsightsPanelOpen();
	const isViewSettingPanelOpen = useIsViewSettingsPanelOpen();

	const isFilterSelectionBarOpen = useShowQuickFilters();

	const showFilterBarToggle = isCompanyManaged;

	const allCardGroups: CardEntryGroup[] = useAllCardGroups();

	const shouldShowSubtasks: boolean = useMemo(() => !isEmpty(allCardGroups), [allCardGroups]);

	const isDueDateEnabled = useBacklogSelector(backlogHasDueDate);

	const hasCardsWithStoryPoint = useBacklogSelector(backlogHasStoryPoints);
	const hasCardsWithEstimate = useBacklogSelector(backlogHasEstimates);

	const hasCardsWithPriority = useBacklogSelector(backlogHasPriority);

	const areAllSubtasksExpanded: boolean = useMemo(
		() => allCardGroups.every((card) => !card.isCollapsed),
		[allCardGroups],
	);

	const areAllSubtasksCollapsed: boolean = useMemo(
		() => allCardGroups.every((card) => card.isCollapsed),
		[allCardGroups],
	);

	const handleExpandSubtasks = useCallback(() => {
		onSubtasksExpandAll(allCardGroups.map((card) => card.parentId.toString()));
	}, [allCardGroups, onSubtasksExpandAll]);

	const handleCollapseSubtasks = useCallback(() => {
		onSubtasksCollapseAll();
	}, [onSubtasksCollapseAll]);

	const cardExtraFields = useBacklogSelector(getCardExtraFields);

	if (getWillShowNav4()) {
		return (
			<FilterBar
				customFiltersQueryRef={customFiltersQueryRef}
				versionFilterRelayQueryRef={versionFilterRelayQueryRef}
			/>
		);
	}

	return (
		<>
			<Wrapper>
				<FilterBarWrapper>
					<FilterBar
						customFiltersQueryRef={customFiltersQueryRef}
						versionFilterRelayQueryRef={versionFilterRelayQueryRef}
					/>
					<Inline space="space.100" alignInline="center">
						<ImportWorkButton />
						{isBacklogWithSprints && (
							<InsightsButton
								onToggle={handleOnToggle}
								isInsightsPanelOpen={isInsightsOpen}
								boardId={rapidViewId}
								edition={appEditions.software}
							/>
						)}
						{isViewSettingAsPanelExpEnabledWithNoExposure() ? (
							isDataLoaded && (
								<ViewSettingsSyncFromLocalStorageWrapper
									boardId={rapidViewId}
									view={View.BACKLOG}
									cardExtraFields={cardExtraFields}
									isVersionsEnabled={isVersionsEnabled}
									isEstimateEnabled={hasCardsWithStoryPoint || hasCardsWithEstimate}
									showEpicAsPanel={showEpicAsPanel}
									isDueDateEnabled={isDueDateEnabled}
									isPriorityEnabled={hasCardsWithPriority}
								>
									<ViewSettingsPanelOnboarding
										isViewSettingPanelOpen={isViewSettingPanelOpen}
										onTogglePanel={handleViewSettingPanelToggle}
									/>
									<ViewSettingsPanelButtonSpotlight>
										<ViewSettingsButton
											isSelected={isViewSettingPanelOpen}
											onClick={handleViewSettingPanelToggle}
										/>
									</ViewSettingsPanelButtonSpotlight>
								</ViewSettingsSyncFromLocalStorageWrapper>
							)
						) : (
							<ViewSettingsPopup
								view={View.BACKLOG}
								boardId={rapidViewId}
								isClassic={isClassic}
								areSubtasksEnabled={isCompanyManaged && shouldShowSubtasks}
								onSubtasksExpandAll={handleExpandSubtasks}
								onSubtasksCollapseAll={handleCollapseSubtasks}
								areAllSubtasksExpanded={areAllSubtasksExpanded}
								areAllSubtasksCollapsed={areAllSubtasksCollapsed}
								showEpicAsPanel={showEpicAsPanel}
								epicCustomName={epicCustomName}
								isVersionsEnabled={isVersionsEnabled}
								showFilterBarToggle={showFilterBarToggle}
							/>
						)}
					</Inline>
				</FilterBarWrapper>
			</Wrapper>
			{isFilterSelectionBarOpen && <FilterSelectionBarContainer />}
		</>
	);
};

export default memo(ControlsBar);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginTop: token('space.negative.100'),
	width: '100%',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterBarWrapper = styled.div({
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	width: '100%',
});
